import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import * as XLSX from "xlsx";

import {
  jsPDF,
} from "jspdf";

import autoTable from "jspdf-autotable";

import {
  getPolizasSeguros,
  getCuotasSeguros,
} from "../helpers/rutaSeguros";

/* =========================================================
   FECHAS
========================================================= */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const firstDayYearISO = () => {
  const d = new Date();

  d.setMonth(0);
  d.setDate(1);

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const fechaISO = (valor) => {
  if (!valor) {
    return "";
  }

  try {
    return new Date(valor)
      .toISOString()
      .slice(0, 10);
  } catch {
    return "";
  }
};

/* =========================================================
   FORMATOS
========================================================= */

const fechaCorta = (valor) => {
  if (!valor) {
    return "-";
  }

  try {
    return new Date(
      valor
    ).toLocaleDateString(
      "es-AR",
      {
        timeZone: "UTC",
      }
    );
  } catch {
    return "-";
  }
};

const money = (valor) =>
  Number(
    valor || 0
  ).toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const numero = (valor) => {
  const n =
    Number(valor);

  return Number.isFinite(n)
    ? n
    : 0;
};

const normalizarTexto = (
  valor
) =>
  String(valor || "")
    .trim()
    .toUpperCase();

const normalizarPatente = (
  valor
) =>
  normalizarTexto(
    valor
  ).replace(
    /[^A-Z0-9]/g,
    ""
  );

/* =========================================================
   ESTADOS
========================================================= */

const estadoBadge = (
  estado
) => {
  switch (
    normalizarTexto(
      estado
    )
  ) {
    case "VIGENTE":
      return "success";

    case "PAGADA":
      return "success";

    case "VENCIDA":
      return "danger";

    case "PENDIENTE":
      return "warning";

    case "ANULADA":
      return "secondary";

    case "REEMPLAZADA":
      return "dark";

    default:
      return "secondary";
  }
};

const medioPagoTexto = (
  valor
) => {
  switch (
    normalizarTexto(
      valor
    )
  ) {
    case "CAJA":
      return "Caja / efectivo";

    case "CUENTA_BANCARIA":
      return "Banco / transferencia";

    case "CHEQUE":
      return "Cheque";

    case "ECHEQ":
      return "eCheq";

    default:
      return valor || "-";
  }
};

/* =========================================================
   COMPONENTE
========================================================= */

const InformeSeguros = () => {
  const [
    polizas,
    setPolizas,
  ] = useState([]);

  const [
    documentos,
    setDocumentos,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    exportando,
    setExportando,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  /* =======================================================
     FILTROS
  ======================================================= */

  const [
    desde,
    setDesde,
  ] = useState(
    firstDayYearISO()
  );

  const [
    hasta,
    setHasta,
  ] = useState(
    todayLocalISO()
  );

  const [
    compania,
    setCompania,
  ] = useState("");

  const [
    nroPoliza,
    setNroPoliza,
  ] = useState("");

  const [
    asegurado,
    setAsegurado,
  ] = useState("");

  const [
    patente,
    setPatente,
  ] = useState("");

  const [
    estadoPoliza,
    setEstadoPoliza,
  ] = useState("");

  const [
    estadoDocumento,
    setEstadoDocumento,
  ] = useState("");

  const [
    filtrosAplicados,
    setFiltrosAplicados,
  ] = useState({
    desde:
      firstDayYearISO(),

    hasta:
      todayLocalISO(),

    compania: "",

    nroPoliza: "",

    asegurado: "",

    patente: "",

    estadoPoliza: "",

    estadoDocumento: "",
  });

  /* =======================================================
     CARGAR DATOS
  ======================================================= */

  const cargar =
    async () => {
      try {
        setLoading(true);

        setError("");

        const [
          rPolizas,
          rDocumentos,
        ] =
          await Promise.all([
            getPolizasSeguros({
              incluirInactivas:
                1,
            }),

            getCuotasSeguros(),
          ]);

        if (
          !rPolizas?.ok
        ) {
          throw new Error(
            rPolizas?.error ||
              "No se pudieron obtener las pólizas."
          );
        }

        if (
          !rDocumentos?.ok
        ) {
          throw new Error(
            rDocumentos?.error ||
              "No se pudieron obtener los documentos."
          );
        }

        setPolizas(
          Array.isArray(
            rPolizas.polizas
          )
            ? rPolizas.polizas
            : []
        );

        setDocumentos(
          Array.isArray(
            rDocumentos.cuotas
          )
            ? rDocumentos.cuotas
            : []
        );
      } catch (err) {
        console.error(
          "Error informe seguros:",
          err
        );

        setError(
          err?.message ||
            "No se pudo cargar el informe."
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    cargar();

    // eslint-disable-next-line
  }, []);

  /* =======================================================
     COMPAÑÍAS PARA COMBO
  ======================================================= */

  const companias =
    useMemo(() => {
      const set =
        new Set();

      polizas.forEach(
        (item) => {
          if (
            item.compania
          ) {
            set.add(
              item.compania
            );
          }
        }
      );

      return Array.from(
        set
      ).sort();
    }, [polizas]);

  /* =======================================================
     APLICAR FILTROS
  ======================================================= */

  const buscar = () => {
    if (
      desde &&
      hasta &&
      desde > hasta
    ) {
      setError(
        "La fecha Desde no puede ser posterior a Hasta."
      );

      return;
    }

    setError("");

    setFiltrosAplicados({
      desde,

      hasta,

      compania,

      nroPoliza:
        nroPoliza.trim(),

      asegurado:
        asegurado.trim(),

      patente:
        patente.trim(),

      estadoPoliza,

      estadoDocumento,
    });
  };

  const limpiar = () => {
    const nuevos = {
      desde:
        firstDayYearISO(),

      hasta:
        todayLocalISO(),

      compania: "",

      nroPoliza: "",

      asegurado: "",

      patente: "",

      estadoPoliza: "",

      estadoDocumento: "",
    };

    setDesde(
      nuevos.desde
    );

    setHasta(
      nuevos.hasta
    );

    setCompania("");

    setNroPoliza("");

    setAsegurado("");

    setPatente("");

    setEstadoPoliza("");

    setEstadoDocumento("");

    setFiltrosAplicados(
      nuevos
    );

    setError("");
  };

  /* =======================================================
     MAPA DE PÓLIZAS
  ======================================================= */

  const mapaPolizas =
    useMemo(() => {
      const mapa =
        new Map();

      polizas.forEach(
        (item) => {
          mapa.set(
            String(
              item._id
            ),
            item
          );
        }
      );

      return mapa;
    }, [polizas]);

  /* =======================================================
     FILTRAR PÓLIZAS
  ======================================================= */

  const polizasFiltradas =
    useMemo(() => {
      const f =
        filtrosAplicados;

      const patenteBuscada =
        normalizarPatente(
          f.patente
        );

      return polizas.filter(
        (item) => {
          if (
            f.compania &&
            normalizarTexto(
              item.compania
            ) !==
              normalizarTexto(
                f.compania
              )
          ) {
            return false;
          }

          if (
            f.nroPoliza &&
            !normalizarTexto(
              item.nroPoliza
            ).includes(
              normalizarTexto(
                f.nroPoliza
              )
            )
          ) {
            return false;
          }

          if (
            f.asegurado &&
            !normalizarTexto(
              item.asegurado
            ).includes(
              normalizarTexto(
                f.asegurado
              )
            )
          ) {
            return false;
          }

          const estado =
            item.estadoCalculado ||
            item.estado;

          if (
            f.estadoPoliza &&
            estado !==
              f.estadoPoliza
          ) {
            return false;
          }

          if (
            patenteBuscada
          ) {
            const encontrada =
              Array.isArray(
                item.vehiculos
              ) &&
              item.vehiculos.some(
                (vehiculo) =>
                  normalizarPatente(
                    vehiculo.patente
                  ).includes(
                    patenteBuscada
                  )
              );

            if (
              !encontrada
            ) {
              return false;
            }
          }

          return true;
        }
      );
    }, [
      polizas,
      filtrosAplicados,
    ]);

  /* =======================================================
     FILAS VEHÍCULOS

     Una fila por vehículo.
     Si una póliza no tiene vehículos,
     igualmente queda visible.
  ======================================================= */

  const filasVehiculos =
    useMemo(() => {
      const filas = [];

      polizasFiltradas.forEach(
        (poliza) => {
          const vehiculos =
            Array.isArray(
              poliza.vehiculos
            )
              ? poliza.vehiculos
              : [];

          if (
            vehiculos.length ===
            0
          ) {
            filas.push({
              polizaId:
                poliza._id,

              compania:
                poliza.compania,

              nroPoliza:
                poliza.nroPoliza,

              asegurado:
                poliza.asegurado,

              patente: "-",

              descripcion:
                "-",

              marcaModelo:
                "-",

              cobertura:
                "-",

              sumaAsegurada:
                0,

              premio:
                0,

              vigenciaDesde:
                poliza.vigenciaDesde,

              vigenciaHasta:
                poliza.vigenciaHasta,

              estado:
                poliza.estadoCalculado ||
                poliza.estado,
            });

            return;
          }

          vehiculos.forEach(
            (vehiculo) => {
              filas.push({
                polizaId:
                  poliza._id,

                compania:
                  poliza.compania,

                nroPoliza:
                  poliza.nroPoliza,

                asegurado:
                  poliza.asegurado,

                patente:
                  vehiculo.patente ||
                  "-",

                descripcion:
                  vehiculo.descripcionSeguro ||
                  vehiculo.vehiculo
                    ?.camion ||
                  "-",

                marcaModelo:
                  vehiculo.marcaModeloSeguro ||
                  [
                    vehiculo.vehiculo
                      ?.marca,
                    vehiculo.vehiculo
                      ?.modelo,
                  ]
                    .filter(
                      Boolean
                    )
                    .join(
                      " "
                    ) ||
                  "-",

                cobertura:
                  vehiculo.cobertura ||
                  "-",

                sumaAsegurada:
                  numero(
                    vehiculo.sumaAsegurada
                  ),

                premio:
                  numero(
                    vehiculo.premio
                  ),

                vigenciaDesde:
                  poliza.vigenciaDesde,

                vigenciaHasta:
                  poliza.vigenciaHasta,

                estado:
                  poliza.estadoCalculado ||
                  poliza.estado,
              });
            }
          );
        }
      );

      return filas;
    }, [
      polizasFiltradas,
    ]);

  /* =======================================================
     DOCUMENTOS FILTRADOS
  ======================================================= */

  const documentosFiltrados =
    useMemo(() => {
      const f =
        filtrosAplicados;

      const patenteBuscada =
        normalizarPatente(
          f.patente
        );

      return documentos.filter(
        (item) => {
          const polizaId =
            typeof item.poliza ===
            "object"
              ? item.poliza?._id
              : item.poliza;

          const poliza =
            mapaPolizas.get(
              String(
                polizaId || ""
              )
            ) ||
            (
              typeof item.poliza ===
              "object"
                ? item.poliza
                : null
            ) ||
            {};

          const fechaVencimiento =
            fechaISO(
              item.vencimiento
            );

          if (
            f.desde &&
            fechaVencimiento &&
            fechaVencimiento <
              f.desde
          ) {
            return false;
          }

          if (
            f.hasta &&
            fechaVencimiento &&
            fechaVencimiento >
              f.hasta
          ) {
            return false;
          }

          if (
            f.compania &&
            normalizarTexto(
              poliza.compania
            ) !==
              normalizarTexto(
                f.compania
              )
          ) {
            return false;
          }

          if (
            f.nroPoliza &&
            !normalizarTexto(
              poliza.nroPoliza
            ).includes(
              normalizarTexto(
                f.nroPoliza
              )
            )
          ) {
            return false;
          }

          if (
            f.asegurado &&
            !normalizarTexto(
              poliza.asegurado
            ).includes(
              normalizarTexto(
                f.asegurado
              )
            )
          ) {
            return false;
          }

          const estadoDoc =
            item.estadoCalculado ||
            item.estado;

          if (
            f.estadoDocumento &&
            estadoDoc !==
              f.estadoDocumento
          ) {
            return false;
          }

          const estadoPol =
            poliza.estadoCalculado ||
            poliza.estado;

          if (
            f.estadoPoliza &&
            estadoPol !==
              f.estadoPoliza
          ) {
            return false;
          }

          if (
            patenteBuscada
          ) {
            const vehiculos =
              Array.isArray(
                poliza.vehiculos
              )
                ? poliza.vehiculos
                : [];

            const existePatente =
              vehiculos.some(
                (vehiculo) =>
                  normalizarPatente(
                    vehiculo.patente
                  ).includes(
                    patenteBuscada
                  )
              );

            if (
              !existePatente
            ) {
              return false;
            }
          }

          return true;
        }
      );
    }, [
      documentos,
      mapaPolizas,
      filtrosAplicados,
    ]);

  /* =======================================================
     KPIs
  ======================================================= */

  const cantidadPolizas =
    polizasFiltradas.length;

  const cantidadVehiculos =
    filasVehiculos.filter(
      (item) =>
        item.patente !==
        "-"
    ).length;

  const polizasVigentes =
    polizasFiltradas.filter(
      (item) =>
        (
          item.estadoCalculado ||
          item.estado
        ) ===
        "VIGENTE"
    ).length;

  const polizasVencidas =
    polizasFiltradas.filter(
      (item) =>
        (
          item.estadoCalculado ||
          item.estado
        ) ===
        "VENCIDA"
    ).length;

  const documentosPendientes =
    documentosFiltrados.filter(
      (item) =>
        [
          "PENDIENTE",
          "VENCIDA",
        ].includes(
          item.estadoCalculado ||
            item.estado
        )
    );

  const documentosPagados =
    documentosFiltrados.filter(
      (item) =>
        (
          item.estadoCalculado ||
          item.estado
        ) ===
        "PAGADA"
    );

  const totalPendiente =
    documentosPendientes.reduce(
      (
        acc,
        item
      ) =>
        acc +
        numero(
          item.importe
        ),
      0
    );

  const totalPagado =
    documentosPagados.reduce(
      (
        acc,
        item
      ) =>
        acc +
        numero(
          item.montoPagado ||
            item.importe
        ),
      0
    );

  /* =======================================================
     EXPORTAR EXCEL
  ======================================================= */

  const exportarExcel = () => {
    try {
      setExportando(true);

      const resumen = [
        {
          Concepto:
            "Pólizas",
          Valor:
            cantidadPolizas,
        },

        {
          Concepto:
            "Pólizas vigentes",
          Valor:
            polizasVigentes,
        },

        {
          Concepto:
            "Pólizas vencidas",
          Valor:
            polizasVencidas,
        },

        {
          Concepto:
            "Vehículos asegurados",
          Valor:
            cantidadVehiculos,
        },

        {
          Concepto:
            "Documentos pendientes",
          Valor:
            documentosPendientes.length,
        },

        {
          Concepto:
            "Total pendiente",
          Valor:
            totalPendiente,
        },

        {
          Concepto:
            "Documentos pagados",
          Valor:
            documentosPagados.length,
        },

        {
          Concepto:
            "Total pagado",
          Valor:
            totalPagado,
        },
      ];

      const detalleVehiculos =
        filasVehiculos.map(
          (item) => ({
            Compañía:
              item.compania,

            Póliza:
              item.nroPoliza,

            Asegurado:
              item.asegurado,

            Patente:
              item.patente,

            Vehículo:
              item.descripcion,

            "Marca / Modelo":
              item.marcaModelo,

            Cobertura:
              item.cobertura,

            "Suma asegurada":
              item.sumaAsegurada,

            "Premio detalle":
              item.premio,

            "Vigencia desde":
              fechaCorta(
                item.vigenciaDesde
              ),

            "Vigencia hasta":
              fechaCorta(
                item.vigenciaHasta
              ),

            Estado:
              item.estado,
          })
        );

      const detalleDocumentos =
        documentosFiltrados.map(
          (item) => {
            const polizaId =
              typeof item.poliza ===
              "object"
                ? item.poliza?._id
                : item.poliza;

            const poliza =
              mapaPolizas.get(
                String(
                  polizaId || ""
                )
              ) ||
              item.poliza ||
              {};

            return {
              Compañía:
                poliza.compania ||
                "",

              Póliza:
                poliza.nroPoliza ||
                "",

              Asegurado:
                poliza.asegurado ||
                "",

              Tipo:
                item.tipoDocumento ||
                "",

              Documento:
                item.nroDocumento ||
                "",

              Cuota:
                item.numeroCuota ||
                "",

              Emisión:
                fechaCorta(
                  item.fechaEmision
                ),

              Vencimiento:
                fechaCorta(
                  item.vencimiento
                ),

              Importe:
                numero(
                  item.importe
                ),

              Estado:
                item.estadoCalculado ||
                item.estado,

              "Fecha pago":
                fechaCorta(
                  item.fechaPago
                ),

              "Monto pagado":
                numero(
                  item.montoPagado
                ),

              "Medio pago":
                medioPagoTexto(
                  item.medioPago
                ),
            };
          }
        );

      const wb =
        XLSX.utils.book_new();

      const wsResumen =
        XLSX.utils.json_to_sheet(
          resumen
        );

      const wsVehiculos =
        XLSX.utils.json_to_sheet(
          detalleVehiculos
        );

      const wsDocumentos =
        XLSX.utils.json_to_sheet(
          detalleDocumentos
        );

      wsResumen["!cols"] = [
        {
          wch: 28,
        },
        {
          wch: 22,
        },
      ];

      wsVehiculos[
        "!cols"
      ] = [
        {
          wch: 22,
        },
        {
          wch: 18,
        },
        {
          wch: 28,
        },
        {
          wch: 12,
        },
        {
          wch: 28,
        },
        {
          wch: 30,
        },
        {
          wch: 35,
        },
        {
          wch: 18,
        },
        {
          wch: 18,
        },
        {
          wch: 15,
        },
        {
          wch: 15,
        },
        {
          wch: 14,
        },
      ];

      wsDocumentos[
        "!cols"
      ] = [
        {
          wch: 22,
        },
        {
          wch: 18,
        },
        {
          wch: 28,
        },
        {
          wch: 16,
        },
        {
          wch: 22,
        },
        {
          wch: 10,
        },
        {
          wch: 14,
        },
        {
          wch: 14,
        },
        {
          wch: 18,
        },
        {
          wch: 14,
        },
        {
          wch: 14,
        },
        {
          wch: 18,
        },
        {
          wch: 24,
        },
      ];

      XLSX.utils.book_append_sheet(
        wb,
        wsResumen,
        "Resumen"
      );

      XLSX.utils.book_append_sheet(
        wb,
        wsVehiculos,
        "Polizas y Vehiculos"
      );

      XLSX.utils.book_append_sheet(
        wb,
        wsDocumentos,
        "Facturacion y Pagos"
      );

      XLSX.writeFile(
        wb,
        `seguros-${filtrosAplicados.desde || "inicio"}-${filtrosAplicados.hasta || "hoy"}.xlsx`
      );
    } catch (err) {
      console.error(
        "Error Excel seguros:",
        err
      );

      setError(
        "No se pudo generar el archivo Excel."
      );
    } finally {
      setExportando(false);
    }
  };

  /* =======================================================
     EXPORTAR PDF
  ======================================================= */

  const exportarPDF = () => {
    try {
      setExportando(true);

      const doc =
        new jsPDF({
          orientation:
            "landscape",

          unit: "mm",

          format: "a4",
        });

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(
        15
      );

      doc.text(
        "Transporte DANLU - Informe de Seguros",
        10,
        12
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(
        8
      );

      doc.text(
        `Vencimientos: ${filtrosAplicados.desde || "Todos"} a ${filtrosAplicados.hasta || "Todos"}`,
        10,
        18
      );

      doc.text(
        `Compañía: ${filtrosAplicados.compania || "Todas"}`,
        10,
        23
      );

      doc.text(
        `Póliza: ${filtrosAplicados.nroPoliza || "Todas"}`,
        80,
        23
      );

      doc.text(
        `Asegurado: ${filtrosAplicados.asegurado || "Todos"}`,
        145,
        23
      );

      doc.text(
        `Patente: ${filtrosAplicados.patente || "Todas"}`,
        230,
        23
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.text(
        `Pólizas: ${cantidadPolizas}`,
        10,
        29
      );

      doc.text(
        `Vehículos: ${cantidadVehiculos}`,
        45,
        29
      );

      doc.text(
        `Pendiente: ${money(
          totalPendiente
        )}`,
        85,
        29
      );

      doc.text(
        `Pagado: ${money(
          totalPagado
        )}`,
        165,
        29
      );

      /* -----------------------------------------------
         PÓLIZAS / VEHÍCULOS
      ----------------------------------------------- */

      autoTable(
        doc,
        {
          startY: 35,

          head: [
            [
              "Compañía",
              "Póliza",
              "Asegurado",
              "Patente",
              "Vehículo",
              "Cobertura",
              "Suma asegurada",
              "Vigencia",
              "Estado",
            ],
          ],

          body:
            filasVehiculos.map(
              (item) => [
                item.compania,

                item.nroPoliza,

                item.asegurado,

                item.patente,

                item.descripcion,

                item.cobertura,

                money(
                  item.sumaAsegurada
                ),

                `${fechaCorta(
                  item.vigenciaDesde
                )} - ${fechaCorta(
                  item.vigenciaHasta
                )}`,

                item.estado,
              ]
            ),

          theme:
            "grid",

          styles: {
            fontSize: 6.5,

            cellPadding: 1.2,

            overflow:
              "linebreak",
          },

          headStyles: {
            fillColor: [
              240,
              240,
              240,
            ],

            textColor: [
              20,
              20,
              20,
            ],

            fontStyle:
              "bold",
          },

          columnStyles: {
            0: {
              cellWidth: 26,
            },

            1: {
              cellWidth: 22,
            },

            2: {
              cellWidth: 35,
            },

            3: {
              cellWidth: 18,
            },

            4: {
              cellWidth: 36,
            },

            5: {
              cellWidth: 52,
            },

            6: {
              cellWidth: 29,

              halign:
                "right",
            },

            7: {
              cellWidth: 38,
            },

            8: {
              cellWidth: 20,
            },
          },
        }
      );

      let y =
        doc.lastAutoTable
          ?.finalY ||
        35;

      y += 8;

      if (
        y > 165
      ) {
        doc.addPage();

        y = 15;
      }

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(
        11
      );

      doc.text(
        "Facturación, vencimientos y pagos",
        10,
        y
      );

      y += 4;

      /* -----------------------------------------------
         DOCUMENTOS
      ----------------------------------------------- */

      autoTable(
        doc,
        {
          startY: y,

          head: [
            [
              "Compañía",
              "Póliza",
              "Documento",
              "Tipo",
              "Vencimiento",
              "Importe",
              "Estado",
              "Fecha pago",
              "Medio",
            ],
          ],

          body:
            documentosFiltrados.map(
              (item) => {
                const polizaId =
                  typeof item.poliza ===
                  "object"
                    ? item.poliza?._id
                    : item.poliza;

                const poliza =
                  mapaPolizas.get(
                    String(
                      polizaId || ""
                    )
                  ) ||
                  item.poliza ||
                  {};

                return [
                  poliza.compania ||
                    "",

                  poliza.nroPoliza ||
                    "",

                  item.nroDocumento ||
                    "-",

                  item.tipoDocumento ||
                    "-",

                  fechaCorta(
                    item.vencimiento
                  ),

                  money(
                    item.importe
                  ),

                  item.estadoCalculado ||
                    item.estado,

                  fechaCorta(
                    item.fechaPago
                  ),

                  medioPagoTexto(
                    item.medioPago
                  ),
                ];
              }
            ),

          theme:
            "grid",

          styles: {
            fontSize: 7,

            cellPadding: 1.3,

            overflow:
              "linebreak",
          },

          headStyles: {
            fillColor: [
              240,
              240,
              240,
            ],

            textColor: [
              20,
              20,
              20,
            ],

            fontStyle:
              "bold",
          },

          columnStyles: {
            0: {
              cellWidth: 28,
            },

            1: {
              cellWidth: 24,
            },

            2: {
              cellWidth: 33,
            },

            3: {
              cellWidth: 25,
            },

            4: {
              cellWidth: 24,
            },

            5: {
              cellWidth: 30,

              halign:
                "right",
            },

            6: {
              cellWidth: 22,
            },

            7: {
              cellWidth: 24,
            },

            8: {
              cellWidth: 43,
            },
          },
        }
      );

      const totalPaginas =
        doc.getNumberOfPages();

      for (
        let p = 1;
        p <= totalPaginas;
        p += 1
      ) {
        doc.setPage(p);

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(
          7
        );

        doc.text(
          `Página ${p} de ${totalPaginas}`,
          287,
          202,
          {
            align:
              "right",
          }
        );
      }

      doc.save(
        `seguros-${filtrosAplicados.desde || "inicio"}-${filtrosAplicados.hasta || "hoy"}.pdf`
      );
    } catch (err) {
      console.error(
        "Error PDF seguros:",
        err
      );

      setError(
        "No se pudo generar el PDF."
      );
    } finally {
      setExportando(false);
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="container-fluid mt-4 mb-4">
      {/* CABECERA */}

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">
            Informe de Seguros
          </h4>

          <small className="text-muted">
            Pólizas, vehículos,
            vencimientos, facturación
            y pagos
          </small>
        </div>

        <div className="mt-2">
          <Button
            variant="success"
            size="sm"
            className="mr-2"
            onClick={
              exportarExcel
            }
            disabled={
              loading ||
              exportando
            }
          >
            Exportar Excel
          </Button>

          <Button
            variant="secondary"
            size="sm"
            className="mr-2"
            onClick={
              exportarPDF
            }
            disabled={
              loading ||
              exportando
            }
          >
            Exportar PDF
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            className="mr-2"
            onClick={
              limpiar
            }
            disabled={
              loading
            }
          >
            Limpiar
          </Button>

          <Button
            variant="dark"
            size="sm"
            onClick={
              buscar
            }
            disabled={
              loading
            }
          >
            Buscar
          </Button>
        </div>
      </div>

      {/* FILTROS */}

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Vencimiento desde
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={
                  desde
                }
                onChange={(
                  e
                ) =>
                  setDesde(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Vencimiento hasta
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={
                  hasta
                }
                onChange={(
                  e
                ) =>
                  setHasta(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Compañía
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={
                  compania
                }
                onChange={(
                  e
                ) =>
                  setCompania(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todas
                </option>

                {companias.map(
                  (item) => (
                    <option
                      key={
                        item
                      }
                      value={
                        item
                      }
                    >
                      {item}
                    </option>
                  )
                )}
              </Form.Control>
            </Col>

            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Póliza
              </Form.Label>

              <Form.Control
                size="sm"
                value={
                  nroPoliza
                }
                placeholder="N° póliza"
                onChange={(
                  e
                ) =>
                  setNroPoliza(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Asegurado
              </Form.Label>

              <Form.Control
                size="sm"
                value={
                  asegurado
                }
                placeholder="Buscar..."
                onChange={(
                  e
                ) =>
                  setAsegurado(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col
              xs={12}
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Patente
              </Form.Label>

              <Form.Control
                size="sm"
                value={
                  patente
                }
                placeholder="Ej.: AE352AD"
                onChange={(
                  e
                ) =>
                  setPatente(
                    e.target.value
                  )
                }
              />
            </Col>
          </Row>

          <Row>
            <Col
              xs={12}
              md={3}
              className="mb-2"
            >
              <Form.Label>
                Estado póliza
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={
                  estadoPoliza
                }
                onChange={(
                  e
                ) =>
                  setEstadoPoliza(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todos
                </option>

                <option value="VIGENTE">
                  Vigente
                </option>

                <option value="VENCIDA">
                  Vencida
                </option>

                <option value="PENDIENTE">
                  Pendiente
                </option>

                <option value="ANULADA">
                  Anulada
                </option>

                <option value="REEMPLAZADA">
                  Reemplazada
                </option>
              </Form.Control>
            </Col>

            <Col
              xs={12}
              md={3}
              className="mb-2"
            >
              <Form.Label>
                Estado documento
              </Form.Label>

              <Form.Control
                as="select"
                size="sm"
                value={
                  estadoDocumento
                }
                onChange={(
                  e
                ) =>
                  setEstadoDocumento(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todos
                </option>

                <option value="PENDIENTE">
                  Pendiente
                </option>

                <option value="VENCIDA">
                  Vencida
                </option>

                <option value="PAGADA">
                  Pagada
                </option>

                <option value="ANULADA">
                  Anulada
                </option>
              </Form.Control>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert
          variant="danger"
          className="py-2"
        >
          {error}
        </Alert>
      )}

      {/* KPIs */}

      <Row className="mb-3">
        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Pólizas
              </small>

              <h4 className="mb-0">
                {cantidadPolizas}
              </h4>

              <small className="text-muted">
                Según filtros
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Vigentes
              </small>

              <h4 className="mb-0 text-success">
                {polizasVigentes}
              </h4>

              <small className="text-muted">
                Vencidas:{" "}
                {
                  polizasVencidas
                }
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Vehículos
              </small>

              <h4 className="mb-0">
                {cantidadVehiculos}
              </h4>

              <small className="text-muted">
                Asegurados
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Pendientes
              </small>

              <h5 className="mb-0 text-danger">
                {money(
                  totalPendiente
                )}
              </h5>

              <small className="text-muted">
                {
                  documentosPendientes.length
                }{" "}
                documento(s)
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Pagado
              </small>

              <h5 className="mb-0 text-success">
                {money(
                  totalPagado
                )}
              </h5>

              <small className="text-muted">
                {
                  documentosPagados.length
                }{" "}
                documento(s)
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col
          xs={6}
          md={2}
          className="mb-2"
        >
          <Card className="h-100">
            <Card.Body>
              <small className="text-muted">
                Documentos
              </small>

              <h4 className="mb-0">
                {
                  documentosFiltrados.length
                }
              </h4>

              <small className="text-muted">
                En el período
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* PÓLIZAS Y VEHÍCULOS */}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              Pólizas y vehículos asegurados
            </h6>

            <small className="text-muted">
              {
                filasVehiculos.length
              }{" "}
              registro(s)
            </small>
          </div>

          <div className="table-responsive">
            <Table
              striped
              hover
              size="sm"
              className="mb-0"
            >
              <thead>
                <tr>
                  <th>
                    Compañía
                  </th>

                  <th>
                    Póliza
                  </th>

                  <th>
                    Asegurado
                  </th>

                  <th>
                    Patente
                  </th>

                  <th>
                    Vehículo
                  </th>

                  <th>
                    Cobertura
                  </th>

                  <th className="text-right">
                    Suma asegurada
                  </th>

                  <th>
                    Vigencia
                  </th>

                  <th>
                    Estado
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                      />

                      <span className="ml-2">
                        Cargando...
                      </span>
                    </td>
                  </tr>
                ) : filasVehiculos.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center text-muted py-4"
                    >
                      No hay pólizas o vehículos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filasVehiculos.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={`${item.polizaId}-${item.patente}-${index}`}
                      >
                        <td className="font-weight-bold">
                          {
                            item.compania
                          }
                        </td>

                        <td>
                          {
                            item.nroPoliza
                          }
                        </td>

                        <td>
                          {
                            item.asegurado
                          }
                        </td>

                        <td className="font-weight-bold">
                          {
                            item.patente
                          }
                        </td>

                        <td>
                          {
                            item.descripcion
                          }

                          {item.marcaModelo !==
                            "-" && (
                            <div>
                              <small className="text-muted">
                                {
                                  item.marcaModelo
                                }
                              </small>
                            </div>
                          )}
                        </td>

                        <td>
                          {
                            item.cobertura
                          }
                        </td>

                        <td className="text-right">
                          {money(
                            item.sumaAsegurada
                          )}
                        </td>

                        <td
                          style={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {fechaCorta(
                            item.vigenciaDesde
                          )}{" "}
                          al{" "}
                          {fechaCorta(
                            item.vigenciaHasta
                          )}
                        </td>

                        <td>
                          <Badge
                            bg={estadoBadge(
                              item.estado
                            )}
                          >
                            {
                              item.estado
                            }
                          </Badge>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* FACTURACIÓN Y PAGOS */}

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              Facturación, vencimientos y pagos
            </h6>

            <small className="text-muted">
              {
                documentosFiltrados.length
              }{" "}
              documento(s)
            </small>
          </div>

          <div className="table-responsive">
            <Table
              striped
              hover
              size="sm"
            >
              <thead>
                <tr>
                  <th>
                    Compañía
                  </th>

                  <th>
                    Póliza
                  </th>

                  <th>
                    Asegurado
                  </th>

                  <th>
                    Tipo
                  </th>

                  <th>
                    Documento
                  </th>

                  <th>
                    Vencimiento
                  </th>

                  <th className="text-right">
                    Importe
                  </th>

                  <th>
                    Estado
                  </th>

                  <th>
                    Fecha pago
                  </th>

                  <th>
                    Medio pago
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                      />

                      <span className="ml-2">
                        Cargando...
                      </span>
                    </td>
                  </tr>
                ) : documentosFiltrados.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted py-4"
                    >
                      No hay documentos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  documentosFiltrados.map(
                    (item) => {
                      const polizaId =
                        typeof item.poliza ===
                        "object"
                          ? item.poliza?._id
                          : item.poliza;

                      const poliza =
                        mapaPolizas.get(
                          String(
                            polizaId ||
                              ""
                          )
                        ) ||
                        item.poliza ||
                        {};

                      const estado =
                        item.estadoCalculado ||
                        item.estado;

                      return (
                        <tr
                          key={
                            item._id
                          }
                        >
                          <td className="font-weight-bold">
                            {
                              poliza.compania
                            }
                          </td>

                          <td>
                            {
                              poliza.nroPoliza
                            }
                          </td>

                          <td>
                            {
                              poliza.asegurado
                            }
                          </td>

                          <td>
                            {
                              item.tipoDocumento
                            }
                          </td>

                          <td>
                            {item.nroDocumento ||
                              "-"}
                          </td>

                          <td>
                            {fechaCorta(
                              item.vencimiento
                            )}
                          </td>

                          <td className="text-right font-weight-bold">
                            {money(
                              item.importe
                            )}
                          </td>

                          <td>
                            <Badge
                              bg={estadoBadge(
                                estado
                              )}
                            >
                              {
                                estado
                              }
                            </Badge>
                          </td>

                          <td>
                            {fechaCorta(
                              item.fechaPago
                            )}
                          </td>

                          <td>
                            {medioPagoTexto(
                              item.medioPago
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )
                )}
              </tbody>

              {!loading &&
                documentosFiltrados.length >
                  0 && (
                  <tfoot>
                    <tr>
                      <td
                        colSpan="6"
                        className="text-right font-weight-bold"
                      >
                        TOTAL DOCUMENTOS
                      </td>

                      <td className="text-right font-weight-bold">
                        {money(
                          documentosFiltrados.reduce(
                            (
                              acc,
                              item
                            ) =>
                              acc +
                              numero(
                                item.importe
                              ),
                            0
                          )
                        )}
                      </td>

                      <td
                        colSpan="3"
                      />
                    </tr>
                  </tfoot>
                )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default InformeSeguros;